import { CardVariant, PricingResult, isVariantOutOfStock } from '../../types/card';

/** "USD 50", or "USD 10 – 100" for a range: how the page names an amount. */
export function variantAmountLabel(variant: Pick<CardVariant, 'currency' | 'faceValue'>): string {
  const { min, max } = variant.faceValue;
  const amount = (value: number) => Math.round(value).toLocaleString();
  return min === max ? `${variant.currency} ${amount(max)}` : `${variant.currency} ${amount(min)} – ${amount(max)}`;
}

export function findMatchingVariant(variants: CardVariant[], value: number): CardVariant | undefined {
  return variants.find(v => v.faceValue.min <= value && v.faceValue.max >= value);
}

export function validateValue(value: number, variant: CardVariant, t: (key: string) => string): string | null {
  const { min, max, step } = variant.faceValue;
  if (value < min) {
    return `${t('pricing.validation.lessThan')} ${variant.currency} ${min.toFixed(2)}`;
  }
  if (value > max) {
    return `${t('pricing.validation.exceed')} ${variant.currency} ${max.toFixed(2)}`;
  }
  if (step) {
    const remainder = Math.round(value * 100) % Math.round(step * 100);
    if (remainder !== 0) {
      return `${t('pricing.validation.step')}: ${step}`;
    }
  }
  return null;
}

// Key of a prefetched price: the card, the face value, the currency and,
// when the variant is sold in named countries, the first of them.
export function priceKey(cardId: string, value: number, currency: string, country?: string): string {
  return country ? `${cardId}:${value}:${currency}:${country}` : `${cardId}:${value}:${currency}`;
}

export function lookupPrice(
  pricingMap: Record<string, PricingResult>,
  cardId: string,
  value: number,
  currency: string,
  country?: string
): PricingResult | undefined {
  return (country ? pricingMap[priceKey(cardId, value, currency, country)] : undefined)
    || pricingMap[priceKey(cardId, value, currency)];
}

export function convertAmount(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rateMap: Record<string, number>
): { converted: number; currency: string; isConverted: boolean } {
  if (fromCurrency === toCurrency || !rateMap[fromCurrency] || !rateMap[toCurrency]) {
    return { converted: amount, currency: fromCurrency, isConverted: false };
  }
  const usdAmount = amount * rateMap[fromCurrency];
  const converted = usdAmount / rateMap[toCurrency];
  return { converted, currency: toCurrency, isConverted: true };
}

// For a range variant, returns the snapped default value (target clamped to
// [min, max] and snapped to the step). For a fixed-denomination variant
// (min === max) returns null - caller should fall through to the variant's
// forced value.
export function pickDefaultCustomValue(variant: CardVariant, target: number): number | null {
  const { min, max, step } = variant.faceValue;
  if (min === max) return null;
  const clamped = Math.min(max, Math.max(min, target));
  if (step && step > 0) {
    const snapped = Math.round(clamped / step) * step;
    const bounded = Math.min(max, Math.max(min, snapped));
    return Math.round(bounded * 100) / 100;
  }
  return Math.round(clamped * 100) / 100;
}

// Effective default value for a variant (snapped target for range, fixed
// value for fixed). Used to key the SSR pricing prefetch.
export function pickDefaultValueForVariant(variant: CardVariant, target: number): number {
  return pickDefaultCustomValue(variant, target) ?? variant.faceValue.max;
}

// Picks the in-stock variant whose default value is closest to the target
// (distance 0 when a range contains it); any variant only if all are out.
export function pickDefaultVariantIndex(variants: CardVariant[], target: number): number {
  if (!variants.length) return 0;
  const closestIndex = (indices: number[]): number => {
    let bestIndex = indices[0];
    let bestDistance = Infinity;
    for (const i of indices) {
      const distance = Math.abs(pickDefaultValueForVariant(variants[i], target) - target);
      if (distance < bestDistance) {
        bestIndex = i;
        bestDistance = distance;
      }
    }
    return bestIndex;
  };
  const inStock = variants.map((_, i) => i).filter((i) => !isVariantOutOfStock(variants[i]));
  return closestIndex(inStock.length ? inStock : variants.map((_, i) => i));
}
